import { t } from "@lingui/macro";
import styled, { css } from "styled-components";
import { Stack } from "~/guidelines/Flex";
import SectionTitle from "~/guidelines/SectionTitle";
import { body1, heading2, heading4 } from "~/guidelines/Typography";

const Container = styled(Stack)`
  align-items: center;
  max-width: 1200px;
  margin: 32px 0px;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(6)};

  @media (min-width: 900px) {
    margin: 80px auto;
    gap: ${({ theme }) => theme.spacing(8)};
  }
`;

const RowsWrapper = styled.div`
  width: calc(100% - 32px);
  max-width: 1200px;
  margin: 0 auto;
  background: ${({ theme }) => theme.color.background.lightThird};
  border: 3px solid ${({ theme }) => theme.color.text.lightFirst};
  overflow: hidden;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;

  @media (min-width: 900px) {
    padding: 40px 0px;
    width: 100%;
  }
`;

const commonRowStyles = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0;
  padding: 0;

  @media (min-width: 900px) {
    gap: 80px;
    padding: 0 40px;
    flex-direction: row;
  }
`;

const FirstRow = styled.div`
  ${commonRowStyles}
  margin-bottom: 0;

  @media (min-width: 900px) {
    margin-bottom: 38.5px;
  }
`;

const SeparatorLine = styled.div`
  height: 3px;
  background-color: ${({ theme }) => theme.color.text.lightFirst};
  width: 100%;
  display: none;

  @media (min-width: 900px) {
    display: block;
  }
`;

const SecondRow = styled.div`
  ${commonRowStyles}
  margin-top: 0;

  @media (min-width: 900px) {
    margin-top: 38.5px;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  order: 1;

  @media (min-width: 900px) {
    align-items: center;
    order: initial;
  }
`;

const Value = styled.div`
  ${heading4}
  color: ${({ theme }) => theme.color.text.lightFirst};
  margin-bottom: 0;
  text-align: right;
  order: 2;
  @media (min-width: 900px) {
    order: 0;
    ${heading2}
  }
`;

const Label = styled.div`
  ${body1}
  text-align: left;

  @media (min-width: 900px) {
    text-align: center;
  }
`;

const Sub = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.color.text.lightSecond};
  text-align: left;
  margin-top: 2px;

  @media (min-width: 900px) {
    text-align: center;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  flex: none;
  width: 100%;

  @media (min-width: 900px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: initial;
  }
`;

const InFigures = () => {
  const FIGURES = [
    { value: "4", label: t`Cities` },
    { value: "8", label: t`Operational establishments` },
    { value: "100", label: t`Chamber in operation` },
    {
      value: "8,7",
      label: t`Average guest score`,
      sub: "(Booking.com, Google, Airbnb)",
    },
    { value: "50", label: t`Hotels by 2028` },
    { value: "€25M", label: t`Assets under management` },
    { value: "+300", label: t`Room in development` },
    { value: "+40K", label: t`Customers welcomed` },
  ];

  const firstRowFigures = FIGURES.slice(0, 4);
  const secondRowFigures = FIGURES.slice(4);

  return (
    <Container>
      <SectionTitle
        variant="normal"
        firstPart={t`In`}
        secondPart={t`Figure`}
        center="all"
      />

      <RowsWrapper>
        <FirstRow>
          {firstRowFigures.map(({ value, label, sub }, i) => (
            <Cell key={`first-${i}`}>
              <Value>{value}</Value>

              <LabelWrapper>
                <Label>{label}</Label>

                {sub && <Sub>{sub}</Sub>}
              </LabelWrapper>
            </Cell>
          ))}
        </FirstRow>

        <SeparatorLine />

        <SecondRow>
          {secondRowFigures.map(({ value, label, sub }, i) => (
            <Cell key={`second-${i}`}>
              <Value>{value}</Value>

              <LabelWrapper>
                <Label>{label}</Label>

                {sub && <Sub>{sub}</Sub>}
              </LabelWrapper>
            </Cell>
          ))}
        </SecondRow>
      </RowsWrapper>
    </Container>
  );
};

export default InFigures;
